/**
 * DescriptionField Component
 *
 * Purpose: Shared multi-line text field for description-style inputs across admin
 *          forms. Standardizes default height (4 rows) and displays a live
 *          "X / max" counter. The max is a soft cap — users can type past it,
 *          but the counter turns red and the field is marked invalid so the
 *          parent form's validation can block save until the user trims the
 *          value back to the limit.
 *
 * Author: Development Team
 * Created: 2026-04-22
 */

import React from 'react';
import { Box, TextField, TextFieldProps, Typography } from '@mui/material';

export const DEFAULT_MAX_LENGTH = 500;
const DEFAULT_MIN_ROWS = 4;

/**
 * Returns a save-blocking error message when `value.length` exceeds `maxLength`,
 * or `null` when the value is within the limit (or empty).
 *
 * Use this from a form's `validateForm()` for every DescriptionField on the form
 * so the 500-char soft cap is enforced consistently — instead of each form
 * re-implementing `value.length > 500` checks (which is what ORCH-151 was fixing:
 * one form had the check, another didn't).
 *
 * The returned string is intentionally short; the user-visible "N over the limit"
 * message is rendered by DescriptionField itself in the helper-text slot, so this
 * value is mostly used as a save-block flag in the parent's errors map.
 */
export function descriptionLengthError(
  value: string | null | undefined,
  maxLength: number = DEFAULT_MAX_LENGTH
): string | null {
  if (value && value.length > maxLength) {
    return `Must be ${maxLength} characters or fewer`;
  }
  return null;
}

export interface DescriptionFieldProps extends Omit<
  TextFieldProps,
  'multiline' | 'minRows' | 'rows' | 'inputProps' | 'helperText' | 'value' | 'onChange'
> {
  value: string;
  onChange: (value: string) => void;
  /** Maximum character count (soft cap — typing past it is allowed but marks the field invalid). Defaults to 500. */
  maxLength?: number;
  /** Minimum visible rows. Defaults to 4. */
  minRows?: number;
  /** Optional helper text displayed below the field (left side). */
  helperText?: React.ReactNode;
}

/**
 * DescriptionField — multi-line textarea with a live character counter.
 *
 * The max length is a soft cap. Users can keep typing past it (so their
 * in-progress thought isn't silently truncated), but the counter turns red
 * and the field is marked `error` / `aria-invalid`. Parent forms should
 * block save when the value length exceeds `maxLength`.
 *
 * The counter is rendered in the MUI `helperText` slot so field spacing
 * matches surrounding inputs. When the component's own `helperText` prop is
 * supplied (e.g. a validation message), it renders alongside the counter on
 * a single baseline.
 */
export const DescriptionField: React.FC<DescriptionFieldProps> = ({
  value,
  onChange,
  maxLength = DEFAULT_MAX_LENGTH,
  minRows = DEFAULT_MIN_ROWS,
  helperText,
  error,
  ...textFieldProps
}) => {
  const length = (value ?? '').length;
  const overLimit = length > maxLength;
  const atLimit = length === maxLength;
  const nearLimit = length >= Math.floor(maxLength * 0.9) && length < maxLength;

  const state: 'normal' | 'warning' | 'error' = overLimit
    ? 'error'
    : atLimit || nearLimit
      ? 'warning'
      : 'normal';

  const counterColor =
    state === 'error' ? 'error.main' : state === 'warning' ? 'warning.main' : 'text.secondary';

  const overLimitMessage = overLimit
    ? `${length - maxLength} over the ${maxLength}-character limit — please shorten to save`
    : null;

  const counter = (
    <Typography
      component="span"
      variant="caption"
      sx={{ color: counterColor, fontVariantNumeric: 'tabular-nums' }}
      data-testid="description-character-count"
      data-state={state}
    >
      {length} / {maxLength}
    </Typography>
  );

  const leftHelper = overLimitMessage ?? helperText;

  const composedHelper = (
    <Box
      component="span"
      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, width: '100%' }}
    >
      <Box component="span" sx={{ flexGrow: 1, minWidth: 0 }}>
        {leftHelper}
      </Box>
      {counter}
    </Box>
  );

  return (
    <TextField
      {...textFieldProps}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      multiline
      minRows={minRows}
      error={error || overLimit}
      helperText={composedHelper}
      slotProps={{
        ...(textFieldProps.slotProps ?? {}),
        formHelperText: {
          component: 'div',
          ...((textFieldProps.slotProps?.formHelperText as Record<string, unknown>) ?? {}),
        },
      }}
    />
  );
};

export default DescriptionField;
